"use client";

import Button from "@/app/components/button";
import Title from "@/app/components/title";
import ToggleList from "@/app/components/toggle-list";
import React, { useState } from "react";
import CubeCalculatorModal from "@/app/components/cube-calculator-modal";

interface CubeCountersProps {
    data: {
        CountersTitle: string;
        Room1Title: string;
        Room2Title: string;
        Room3Title: string;
        Room4Title: string;
        Room5Title: string;
        Room6Title: string;
        Room7Title: string;
        Room8Title: string;
        Room9Title: string;
        Room1Items: { id: number; Item: string }[];
        Room2Items: { id: number; Item: string }[];
        Room3Items: { id: number; Item: string }[];
        Room4Items: { id: number; Item: string }[];
        Room5Items: { id: number; Item: string }[];
        Room6Items: { id: number; Item: string }[];
        Room7Items: { id: number; Item: string }[];
        Room8Items: { id: number; Item: string }[];
        Room9Items: { id: number; Item: string }[];
        BtnTitle: string;
    };
    lang: string;
}

const CubeCounters: React.FC<CubeCountersProps> = ({ data, lang }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [values, setValues] = useState<{[key: number]: number}>({});
    
    const handleValueChange = (id: number, value: number) => {
        setValues(prev => ({
            ...prev,
            [id]: value
        }));
    };

    const getFilledItems = () => {
        const result: Array<{
            room: string;
            item: string;
            value: number;
        }> = [];

        for (let i = 1; i <= 9; i++) {
            const roomTitle = data[`Room${i}Title` as keyof typeof data];
            const roomItems = data[`Room${i}Items` as keyof typeof data] as {id: number, Item: string}[];

            if (roomTitle && roomItems) {
                roomItems.forEach(item => {
                    const value = values[item.id] || 0;
                    if (value > 0) {
                        result.push({
                            room: roomTitle as string,
                            item: item.Item,
                            value: value
                        });
                    }
                });
            }
        }

        return result;
    };

    const calculateTotalSpace = () => {
        // Объемы для английской версии
        const itemVolumesEN: Record<string, number> = {
            // Kitchen
            "Kitchen table": 0.6,
            "Kitchen chair": 0.2,
            "Sideboard": 1,
            "Dishwasher": 0.8,
            "Fridge": 1.6,
            "Freezer": 1.6,
            "Shelves": 0.6,
            "Microwave": 0.2,
            
            // Office
            "Desk": 0.8,
            "Chair": 0.3,
            "Bookcases": 0.6,
            "Cabinet": 1,
            "Wardrobe": 1,
            "Drawer": 0.7,
            "PC (Personal computer)": 0.3,
            
            // Bedroom
            "Doublebed (with frame)": 1,
            "Bed mattress": 0.6,
            "Besides table": 0.2,
            "Kids bed": 0.8,
            "Changing table": 0.4,
            
            // Garden/garage
            "Stroller": 0.3,
            "Bicycle": 0.35,
            "Tires": 0.6,
            "Garden furniture": 1,
            "Garden tools": 0.3,
            "Gas grill": 1,
            "Golf bag": 0.3,
            "Parasol": 0.5,
            "Skis": 0.1,
            "Wheel barrow": 0.5,
            
            // Living room
            "Sofa 2 seat": 1,
            "Sofa 3 seat": 1.5,
            "Armchair": 0.6,
            "Coffe table": 0.5,
            "Side table": 0.3,
            "TV": 0.4,
            "TV Bench": 1,
            "Dining table": 0.8,
            
            // Toilet/bathroom
            "Shelf": 0.4,
            "Washing machine": 0.6,
            "Dryer": 0.6,
            
            // Other
            "Moving boxes": 0.12,
            "Filled Bags": 0.2,
            "Piano": 1.2,
            "Chandellier": 0.6,
            "Safe": 1,
            "Picture boxes": 0.2,
            "Carpet": 0.2,
            "Speaker": 0.2,
            "Moving wardrobe": 0.4,
            "Floor lamps": 0.2
        };

        // Volumes for the Swedish version
        const itemVolumesSW: Record<string, number> = {
            // Kök
            "Köksbord": 0.6,
            "Köksstol": 0.2,
            "Skänk": 1,
            "Diskmaskin": 0.8,
            "Kyl": 1.6,
            "Frys": 1.6,
            "Hyllor": 0.6,
            "Mikrougn": 0.2,
            
            // Arbetsrum/Kontor
            "Skrivbord": 0.8,
            "Stol": 0.3,
            "Bokhyllor": 0.6,
            "Skåp": 1,
            "Garderob": 1,
            "Byrå": 0.7,
            "PC (Personal computer)": 0.3,
            
            // Bedroom/Sovrum
            "Säng. dubbel inkl ram": 1,
            "Sängmadrass": 0.6,
            "sängbord": 0.2,
            "Barnsäng": 0.8,
            "Skötbord": 0.4,
            
            // Förråd/garage
            "barnvagn": 0.3,
            "Cykel": 0.35,
            "Däck": 0.6,
            "Trädgårdsmöbler": 1,
            "Trädgårdsredskap": 0.3,
            "Gasol grill": 1,
            "Golf bag": 0.3,
            "Parasoll": 0.5,
            "Skidor": 0.1,
            "skottskärra": 0.5,
            
            // Living room/Vardagsrum
            "Soffa 2 sitts": 1,
            "Soffa 3 sitts": 1.5,
            "Fåtölj": 0.6,
            "Soffbord": 0.5,
            "Sidobord": 0.3,
            "TV": 0.4,
            "TV-Bänk": 1,
            "Matbord": 0.8,
            
            // bathroom
            "Hylla": 0.4,
            "Tvättmaskin": 0.6,
            "Torktumlare": 0.6,
            
            // Övrigt
            "Flyttlådor": 0.12,
            "Fyllda Säckar": 0.2,
            "Piano": 1.2,
            "Takkrona": 0.6,
            "kassaskåp": 1,
            "Tavellådor": 0.2,
            "Matta": 0.2,
            "Högtalare": 0.2,
            "Flytt garderober": 0.4,
            "Golvlampor": 0.2
        };

        const volumes = lang === 'en' ? itemVolumesEN : itemVolumesSW;
        const filledItems = getFilledItems();
        
        return filledItems.reduce((sum, item) => {
            const volume = volumes[item.item] || 0.5;
            if (!volumes[item.item]) {
                console.warn(`Volume not found for item: ${item.item} (${lang})`);
            }
            return sum + (item.value * volume);
        }, 0);
    };

    return (
        <section className="mt-[72px] mb-[128px] max-[576px]:mt-[32px] max-[576px]:mb-[64px]">
            <div className="container max-w-[744px] mx-auto ">
                <div className="mb-[32px] max-[576px]:mb-[24px]">
                    <Title fontSize="text-[38px] max-[576px]:text-[22px]" text={data.CountersTitle} position="left"/>
                </div>
                <div className="space-y-6 max-[576px]:space-y-[16px]">
                    {data.Room1Title && <ToggleList 
                        title={data.Room1Title} 
                        items={data.Room1Items}
                        values={values}
                        onValueChange={handleValueChange}
                    />}
                    {data.Room2Title && <ToggleList 
                        title={data.Room2Title} 
                        items={data.Room2Items}
                        values={values}
                        onValueChange={handleValueChange}
                    />}
                    {data.Room3Title && <ToggleList 
                        title={data.Room3Title} 
                        items={data.Room3Items}
                        values={values}
                        onValueChange={handleValueChange}
                    />}
                    {data.Room4Title && <ToggleList 
                        title={data.Room4Title} 
                        items={data.Room4Items}
                        values={values}
                        onValueChange={handleValueChange}
                    />}
                    {data.Room5Title && <ToggleList 
                        title={data.Room5Title} 
                        items={data.Room5Items}
                        values={values}
                        onValueChange={handleValueChange}
                    />}
                    {data.Room6Title && <ToggleList 
                        title={data.Room6Title} 
                        items={data.Room6Items}
                        values={values}
                        onValueChange={handleValueChange}
                    />}
                    {data.Room7Title && <ToggleList 
                        title={data.Room7Title} 
                        items={data.Room7Items}
                        values={values}
                        onValueChange={handleValueChange}
                    />}
                    {data.Room8Title && <ToggleList 
                        title={data.Room8Title} 
                        items={data.Room8Items}
                        values={values}
                        onValueChange={handleValueChange}
                    />}
                    {data.Room9Title && <ToggleList 
                        title={data.Room9Title} 
                        items={data.Room9Items}
                        values={values}
                        onValueChange={handleValueChange}
                    />}
                </div>
                <div className="mt-[40px] max-[576px]:mt-[16px]">
                    <Button 
                        type="primary" 
                        text={data.BtnTitle} 
                        size="big" 
                        fontSize="big" 
                        mobileFull="yes"
                        onClick={() => {
                            const hasItems = getFilledItems().length > 0;
                            if (hasItems) {
                                setIsModalOpen(true);
                            } else {
                                alert(lang === 'en' ? "Please fill at least one item" : "Vänligen fyll i minst en artikel");
                            }
                        }}
                    />
                </div>
            </div>
            
            {isModalOpen && (
                <div 
                    className={`fixed inset-0 flex items-center justify-center bg-[#00000033] z-20 transition-opacity duration-500 ease-in-out
                    ${isModalOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
                >
                    <div 
                        className={`transform transition-transform duration-500 ease-in-out
                        ${isModalOpen ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"}`}
                    >
                        <CubeCalculatorModal
                            isOpen={isModalOpen}
                            onClose={() => setIsModalOpen(false)}
                            items={getFilledItems()}
                            totalSpace={calculateTotalSpace()}
                            lang={lang}
                        />
                    </div>
                </div>
            )}
        </section>
    );
};

export default CubeCounters;